import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Portal,
    TextField,
    IconButton,
    Avatar,
    Tooltip,
} from '@mui/material';
import {
    Edit as EditIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import styles from './ItemSelectionModal.module.scss';

/**
 * ItemSelectionModal - A reusable modal for selecting and editing items
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls modal visibility
 * @param {function} props.onClose - Callback when modal is closed
 * @param {function} props.onConfirm - Callback when item is confirmed with signature: (item, customName) => void
 * @param {Array} props.items - Array of items to select from
 * @param {Object} props.selectedItem - Currently selected item (optional)
 * @param {string} props.direction - Text direction ('rtl' or 'ltr')
 * @param {string} props.initialItemName - Initial name for the item (optional)
 */
const ItemSelectionModal = ({
    open,
    onClose,
    onConfirm,
    items = [],
    selectedItem: initialSelectedItem = null,
    direction: directionProp,
    initialItemName = '',
}) => {
    const { t, i18n } = useTranslation();
    const direction = directionProp || i18n.dir() || 'rtl';
    const [selectedItem, setSelectedItem] = useState(null);
    const [customItemName, setCustomItemName] = useState('');

    const formattedItems = useMemo(() => {
        return (items || []).map((item, index) => {
            const makat = String(item?.makat ?? item?.Makat ?? '').trim();
            const name = String(item?.name ?? item?.Name ?? '').trim();
            const id = item?.id ?? item?.Id ?? '';
            const value = makat || String(id || `item-${index}`);
            const label = name && makat
                ? `${name} - ${makat}`
                : (name || makat || value);
            return {
                ...item,
                makat,
                name,
                value,
                label,
            };
        });
    }, [items]);

    useEffect(() => {
        if (open) {
            setSelectedItem(initialSelectedItem);
            setCustomItemName(initialItemName || initialSelectedItem?.name || '');
        }
    }, [open, initialSelectedItem, initialItemName]);

    const handleSelectItem = (_event, item) => {
        setSelectedItem(item || null);
        setCustomItemName(item?.name || '');
    };

    const handleClose = () => {
        setSelectedItem(null);
        setCustomItemName('');
        onClose();
    };

    const handleConfirm = () => {
        if (selectedItem && onConfirm) {
            onConfirm(selectedItem, customItemName);
            handleClose();
        }
    };

    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        startAdornment: direction === 'ltr'
            ? <EditIcon sx={{ fontSize: 16, color: 'var(--xtram-mirage-muted)', marginInlineEnd: '6px' }} />
            : null,
        endAdornment: direction === 'rtl'
            ? <EditIcon sx={{ fontSize: 16, color: 'var(--xtram-mirage-muted)', marginInlineStart: '6px' }} />
            : null,
        sx: {
            height: '42px',
            padding: '0 14px',
            borderRadius: 'var(--xtram-radius-md)',
            background: 'var(--item-field-bg, #f5f6fd)',
            border: 'none',
            boxSizing: 'border-box',
        },
    };

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
        },
    };

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={handleClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.scroll}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || t('Cancel')}>
                                    <IconButton onClick={handleClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('SelectItem')}</div>
                                <div />
                            </div>

                            <div className={styles.field}>
                                <div className={styles.title}>{t('SearchItems')}</div>
                                <div className={styles.combo}>
                                    <AutocompleteSelectFilter
                                        options={formattedItems}
                                        selectedValues={
                                            selectedItem
                                                ? (formattedItems.find(
                                                    (item) =>
                                                        String(item.value) === String(selectedItem.makat ?? selectedItem.value)
                                                        || String(item.makat) === String(selectedItem.makat ?? '')
                                                ) || {
                                                    value: selectedItem.makat ?? selectedItem.value,
                                                    label: selectedItem.label
                                                        || `${selectedItem.name || ''} - ${selectedItem.makat || ''}`.trim(),
                                                    ...selectedItem,
                                                })
                                                : null
                                        }
                                        onChange={handleSelectItem}
                                        placeholder={t('SearchItems')}
                                        isMultiple={false}
                                        direction={direction}
                                        searchable
                                        clearable
                                        portalMenu
                                        portalZIndex={2200}
                                    />
                                </div>
                            </div>

                            <div className={styles.selectedPanel}>
                                <div className={styles.titleRow}>
                                    <CheckCircleIcon className={styles.titleIcon} />
                                    <span>{t('SelectedItem')}</span>
                                </div>

                                <div className={styles.field} style={{ marginBottom: 0 }}>
                                    <div className={styles.titleRow}>
                                        <EditIcon className={styles.titleIcon} />
                                        <span>
                                            {t('ItemDescription')} ({t('Editable')})
                                        </span>
                                    </div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('EnterCustomItemName')}
                                        value={customItemName}
                                        onChange={(e) => setCustomItemName(e.target.value)}
                                        variant="standard"
                                        className={styles.styleInput}
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                        disabled={!selectedItem}
                                    />
                                    <div className={styles.hint}>{t('EditItemNameHint')}</div>
                                </div>

                                <div className={styles.detailsCard}>
                                    {selectedItem?.imageUrl && (
                                        <div className={styles.imageWrap}>
                                            <Avatar
                                                src={selectedItem.imageUrl}
                                                variant="rounded"
                                                sx={{
                                                    width: 100,
                                                    height: 100,
                                                    border: '2px solid var(--xtram-blue)',
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>{t('OriginalName')}:</span>
                                        <span className={styles.detailValue}>{selectedItem?.name || '—'}</span>
                                    </div>
                                    <div className={`${styles.detailRow} ${styles.detailRowMakat}`}>
                                        <span className={styles.detailLabel}>{t('Makat')}:</span>
                                        <span className={`${styles.detailValue} ${styles.detailValueAccent}`}>
                                            {selectedItem?.makat || '—'}
                                        </span>
                                    </div>
                                    <div className={`${styles.detailRow} ${styles.detailRowPrice}`}>
                                        <span className={styles.detailLabel}>{t('Price')}:</span>
                                        <span className={`${styles.detailValue} ${styles.detailValuePrice}`}>
                                            {selectedItem?.price ?? '—'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.footer}>
                                <button type="button" className={styles.cancel} onClick={handleClose}>
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="button"
                                    className={styles.save}
                                    onClick={handleConfirm}
                                    disabled={!selectedItem}
                                >
                                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                                    {t('Confirm')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default ItemSelectionModal;
